import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function KeywordTracker() {
  const [keywords, setKeywords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [googleConfigured, setGoogleConfigured] = useState(false);
  const [siteUrl, setSiteUrl] = useState('https://delanci.com/');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchKeywords();
    checkGoogleStatus();
  }, []);

  const checkGoogleStatus = async () => {
    try {
      const data = await apiFetch('/api/keywords/google-status');
      setGoogleConfigured(data.configured);
    } catch (error) {
      console.error('Google status check error:', error);
      setError(error.message);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const data = await apiFetch('/api/keywords/google-auth');
      if (data.authUrl) {
        window.open(data.authUrl, '_blank');
      }
    } catch (error) {
      console.error('Google auth error:', error);
      setError(error.message);
    }
  };

  const fetchKeywords = async () => {
    try {
      const data = await apiFetch('/api/keywords');
      setKeywords(data.keywords || []);
      setSummary(data.summary);
    } catch (error) {
      console.error('Fetch keywords error:', error);
      setError(error.message);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;

    try {
      const data = await apiFetch('/api/keywords/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: newKeyword.trim(), siteUrl: googleConfigured ? siteUrl : undefined })
      });
      setKeywords((previous) => [data, ...previous]);
      setNewKeyword('');
      setError('');
    } catch (error) {
      console.error('Add keyword error:', error);
      setError(error.message);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedKeyword) return;

    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/keywords/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: selectedKeyword.keyword })
      });
      setAnalysis(data);
    } catch (error) {
      console.error('Analyze error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (keywordId) => {
    try {
      const data = await apiFetch('/api/keywords/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keywordId,
          siteUrl: googleConfigured ? siteUrl : undefined
        })
      });
      setKeywords((previous) => previous.map(k => k.id === data.id ? data : k));
      setError('');
    } catch (error) {
      console.error('Track error:', error);
      setError(error.message);
    }
  };

  const handleRefreshAll = async () => {
    if (keywords.length === 0 || refreshingAll) return;

    setRefreshingAll(true);
    try {
      // 逐个刷新关键词，避免一次性并发太多请求触发 GSC 限流
      const updated = [...keywords];
      for (let i = 0; i < updated.length; i++) {
        const k = updated[i];
        try {
          const data = await apiFetch('/api/keywords/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              keywordId: k.id,
              siteUrl: googleConfigured ? siteUrl : undefined
            })
          });
          updated[i] = data;
          // 每刷一个新数据就更新一次界面，让用户看到进度
          setKeywords([...updated]);
        } catch (error) {
          console.error(`Refresh keyword ${k.keyword} error:`, error);
          setError(`${k.keyword}：${error.message}`);
        }
      }
      fetchKeywords(); // 最后刷新一次汇总
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleDelete = async (keywordId) => {
    try {
      await apiFetch(`/api/keywords/${keywordId}`, { method: 'DELETE' });
      setKeywords((previous) => previous.filter(k => k.id !== keywordId));
      if (selectedKeyword?.id === keywordId) {
        setSelectedKeyword(null);
        setAnalysis(null);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setError(error.message);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return { icon: '↑', color: '#28a745', text: '上升' };
      case 'down': return { icon: '↓', color: '#dc3545', text: '下降' };
      case 'new': return { icon: '+', color: '#0084ff', text: '新增' };
      case 'stable': return { icon: '→', color: '#666', text: '稳定' };
      default: return { icon: '-', color: '#666', text: '未知' };
    }
  };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h2 style={{ fontSize: '18px', margin: '0 0 20px', color: '#1a1a1a' }}>📈 关键词排名跟踪</h2>
      {error && (
        <div style={{ padding: '10px 12px', marginBottom: '15px', backgroundColor: '#fdecea', color: '#c62828', borderRadius: '6px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' }}>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0084ff' }}>{summary.total}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>关键词总数</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{summary.improved}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>排名上升</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>{summary.dropped}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>排名下降</div>
          </div>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>{summary.averageRank}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>平均排名</div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {googleConfigured && (
          <>
            <span style={{ fontSize: '13px', color: '#1565c0', whiteSpace: 'nowrap' }}>站点域名：</span>
            <input
              type="text"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="https://你的正式域名.com"
              style={{
                width: '220px',
                padding: '9px 10px',
                border: '1px solid #90caf9',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </>
        )}
        <input
          type="text"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
          placeholder="输入关键词进行跟踪..."
          style={{
            width: '280px',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={handleAddKeyword}
          disabled={!newKeyword.trim()}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: '#0084ff',
            color: '#fff',
            cursor: newKeyword.trim() ? 'pointer' : 'not-allowed',
            fontSize: '14px'
          }}
        >
          + 添加关键词
        </button>
        {!googleConfigured && (
          <button
            onClick={handleGoogleAuth}
            style={{
              padding: '10px 20px',
              border: '1px solid #4285f4',
              borderRadius: '6px',
              backgroundColor: '#fff',
              color: '#4285f4',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px' }}>🔍</span>
            连接 Google
          </button>
        )}
        {googleConfigured && (
          <span style={{ padding: '10px 15px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '6px', fontSize: '13px' }}>
            ✅ 已连接 Google
          </span>
        )}
        <button
          onClick={handleRefreshAll}
          disabled={refreshingAll || keywords.length === 0}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: refreshingAll ? '#90caf9' : '#ff9800',
            color: '#fff',
            cursor: (refreshingAll || keywords.length === 0) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            whiteSpace: 'nowrap'
          }}
        >
          {refreshingAll ? '⏳ 刷新中...' : '🔄 批量刷新'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div>
          <h4 style={{ margin: '0 0 15px', fontSize: '14px', color: '#666' }}>关键词列表</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #eee' }}>
                <th style={{ textAlign: 'left', padding: '10px 0', fontWeight: '500', color: '#333' }}>关键词</th>
                <th style={{ textAlign: 'center', padding: '10px 0', fontWeight: '500', color: '#333' }}>排名</th>
                <th style={{ textAlign: 'center', padding: '10px 0', fontWeight: '500', color: '#333' }}>趋势</th>
                <th style={{ textAlign: 'center', padding: '10px 0', fontWeight: '500', color: '#333' }}>展示量</th>
                <th style={{ textAlign: 'right', padding: '10px 0', fontWeight: '500', color: '#333' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {keywords.map(k => {
                const trend = getTrendIcon(k.trend);
                return (
                  <tr
                    key={k.id}
                    onClick={() => {
                      setSelectedKeyword(k);
                      setAnalysis(null);
                    }}
                    style={{
                      borderBottom: '1px solid #f5f5f5',
                      cursor: 'pointer',
                      backgroundColor: selectedKeyword?.id === k.id ? '#e3f2fd' : '#fff'
                    }}
                  >
                    <td style={{ padding: '10px 0', color: '#333' }}>{k.keyword}</td>
                    <td style={{ padding: '10px 0', textAlign: 'center', color: '#0084ff', fontWeight: '500' }}>
                      {k.rank || '-'}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'center' }}>
                      <span style={{ color: trend.color, fontWeight: '500' }}>{trend.icon} {trend.text}</span>
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'center', color: '#666' }}>{k.volume}</td>
                    <td style={{ padding: '10px 0', textAlign: 'right' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrack(k.id);
                        }}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '5px'
                        }}
                      >
                        刷新
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(k.id);
                        }}
                        style={{
                          padding: '5px 10px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div>
          <h4 style={{ margin: '0 0 15px', fontSize: '14px', color: '#666' }}>关键词分析</h4>

          {selectedKeyword ? (
            <div>
              <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '15px' }}>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>"{selectedKeyword.keyword}"</div>
                {selectedKeyword.rank && (
                  <div style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
                    当前排名: <span style={{ color: '#0084ff', fontWeight: '500' }}>第 {selectedKeyword.rank} 位</span>
                  </div>
                )}
              </div>

              <button
                onClick={handleAnalyze}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#0084ff',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  marginBottom: '15px'
                }}
              >
                {loading ? '🤖 分析中...' : '🤖 AI分析关键词'}
              </button>

              {analysis && (
                <div style={{ padding: '15px', border: '1px solid #eee', borderRadius: '6px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>搜索量</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: '#28a745' }}>{analysis.searchVolume}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>竞争难度</div>
                      <div style={{ fontSize: '16px', fontWeight: '500', color: analysis.competition === 'High' ? '#dc3545' : '#ffc107' }}>
                        {analysis.competition} ({analysis.difficultyScore}/100)
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>优化建议</div>
                    <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '14px', color: '#333' }}>
                      {analysis.optimizationTips.map((tip, idx) => (
                        <li key={idx} style={{ marginBottom: '5px' }}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>推荐关键词</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {analysis.relatedKeywords.map((kw, idx) => (
                        <span
                          key={idx}
                          onClick={() => setNewKeyword(kw)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#e3f2fd',
                            color: '#0084ff',
                            borderRadius: '15px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          + {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '6px', color: '#666' }}>
              请选择一个关键词进行分析
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default KeywordTracker;
